import { type FC, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageTitle } from "../../../../components/PageTitle";
import {
  useAddContactsToGroupApi,
  useContactsApi,
  useGroupApi,
} from "../../../../hooks/use-distribution-api";

export const AddContactsPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: group, loading: groupLoading } = useGroupApi({
    id: id || "",
    skip: !id,
  });

  const {
    data: contactsData,
    loading: contactsLoading,
    error: contactsError,
  } = useContactsApi({
    limit: 100, // Backend max is 100
  });

  const { addContacts, loading: addLoading } = useAddContactsToGroupApi();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");

  // Filter out contacts that are already in the group
  const existingContactIds = new Set(group?.contacts.map(c => c.id) || []);
  const availableContacts =
    contactsData?.items.filter(c => !existingContactIds.has(c.id)) || [];

  // Filter by search term
  const filteredContacts = availableContacts.filter(contact => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      contact.companyName.toLowerCase().includes(term) ||
      (contact.contactName?.toLowerCase().includes(term) ?? false) ||
      contact.email.toLowerCase().includes(term)
    );
  });

  const handleToggle = (contactId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(contactId)) {
      newSelected.delete(contactId);
    } else {
      newSelected.add(contactId);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredContacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredContacts.map(c => c.id)));
    }
  };

  const handleSubmit = async () => {
    if (!id || selectedIds.size === 0) return;

    try {
      await addContacts(id, Array.from(selectedIds));
      navigate(`/admin/distribution/groups/${id}`);
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    }
  };

  if (groupLoading || contactsLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>連絡先を追加</PageTitle>
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </div>
    );
  }

  if (contactsError) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>連絡先を追加</PageTitle>
        <div className="alert alert-error">
          <span>連絡先の取得に失敗しました: {contactsError.message}</span>
        </div>
        <Link to="/admin/distribution/groups" className="btn btn-ghost mt-4">
          一覧に戻る
        </Link>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>連絡先を追加</PageTitle>
        <div className="alert alert-error">
          <span>グループが見つかりません</span>
        </div>
        <Link to="/admin/distribution/groups" className="btn btn-ghost mt-4">
          一覧に戻る
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex items-center gap-4 mb-6">
        <Link
          to={`/admin/distribution/groups/${id}`}
          className="btn btn-ghost btn-sm"
        >
          ← 戻る
        </Link>
        <PageTitle>{group.name} に連絡先を追加</PageTitle>
      </div>

      {/* Search and actions */}
      <div className="flex justify-between items-center mb-4">
        <div className="form-control">
          <input
            type="text"
            placeholder="検索..."
            className="input input-bordered w-64"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-base-content/60">
            {selectedIds.size}件選択中
          </span>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={selectedIds.size === 0 || addLoading}
          >
            {addLoading ? (
              <span className="loading loading-spinner loading-sm" />
            ) : (
              `${selectedIds.size}件追加`
            )}
          </button>
        </div>
      </div>

      {/* Contact list */}
      <div className="card bg-base-100 shadow-md">
        <div className="card-body p-0">
          {filteredContacts.length === 0 ? (
            <div className="text-center py-8 text-base-content/60">
              {availableContacts.length === 0 ? (
                <div>
                  <p>追加可能な連絡先がありません</p>
                  <p className="text-xs mt-2">
                    全連絡先数: {contactsData?.items.length ?? 0}件 /
                    このグループの連絡先数: {group.contacts.length}件
                  </p>
                </div>
              ) : (
                "検索結果がありません"
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      <label>
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={
                            filteredContacts.length > 0 &&
                            selectedIds.size === filteredContacts.length
                          }
                          onChange={handleSelectAll}
                        />
                      </label>
                    </th>
                    <th>会社名</th>
                    <th>担当者名</th>
                    <th>メールアドレス</th>
                    <th>既存グループ</th>
                    <th>状態</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map(contact => (
                    <tr
                      key={contact.id}
                      className={`hover cursor-pointer ${
                        selectedIds.has(contact.id) ? "bg-primary/10" : ""
                      }`}
                      onClick={() => handleToggle(contact.id)}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <label>
                          <input
                            type="checkbox"
                            className="checkbox"
                            checked={selectedIds.has(contact.id)}
                            onChange={() => handleToggle(contact.id)}
                          />
                        </label>
                      </td>
                      <td>{contact.companyName}</td>
                      <td>{contact.contactName || "-"}</td>
                      <td>{contact.email}</td>
                      <td>
                        {contact.groups.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {contact.groups.map(g => (
                              <span
                                key={g.id}
                                className="badge badge-xs badge-ghost"
                              >
                                {g.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-base-content/40">なし</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge badge-sm ${
                            contact.isActive ? "badge-success" : "badge-error"
                          }`}
                        >
                          {contact.isActive ? "有効" : "無効"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Bottom action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-base-200 border-t p-4">
          <div className="container mx-auto flex justify-end items-center gap-4">
            <span>{selectedIds.size}件の連絡先を選択中</span>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setSelectedIds(new Set())}
            >
              選択解除
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={addLoading}
            >
              {addLoading ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                "グループに追加"
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
